/**
 * FreePlanAccountsTable - Free plan account intelligence table
 *
 * Features:
 * - All non-cancelled accounts on free (tierLevel=0) plan
 * - Days on free plan with color-coded urgency badges
 * - Client-side search filter
 * - Status pills and user count
 * - Clickable rows navigate to account detail
 * - Framer Motion row entrance animations
 */
import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle,
  ChevronRight,
  RefreshCw,
  Search,
  UserCheck,
  Users,
} from 'lucide-react';
import { Button, Card, CardContent, CardHeader, Input, Skeleton, cn } from '@respectly/ui';
import type { FreePlanAccount } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface FreePlanAccountsTableProps {
  accounts: FreePlanAccount[];
  isLoading: boolean;
  error: { code: string; message: string } | null;
  onViewAccount: (tenantId: string) => void;
  onRetry: () => void;
}

// ============================================
// Formatters
// ============================================

const numberFormatter = new Intl.NumberFormat('en-US');

function formatDate(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(date));
}

// ============================================
// DaysOnFreeBadge
// ============================================

function DaysOnFreeBadge({ days }: { days: number }) {
  if (days < 30) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
        {days}d
      </span>
    );
  }
  if (days < 90) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
        {days}d
      </span>
    );
  }
  if (days < 180) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400">
        {days}d
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
      <AlertTriangle className="h-3 w-3" />
      {days}d
    </span>
  );
}

// ============================================
// StatusPill
// ============================================

function StatusPill({ status }: { status: string }) {
  if (status === 'active') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
        Active
      </span>
    );
  }
  if (status === 'suspended') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400">
        Suspended
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
      {status}
    </span>
  );
}

// ============================================
// Row urgency helper for background tint
// ============================================

function getRowUrgencyClass(days: number): string {
  if (days >= 180) return 'hover:bg-red-50/60 dark:hover:bg-red-950/20';
  if (days >= 90) return 'hover:bg-orange-50/60 dark:hover:bg-orange-950/20';
  return 'hover:bg-slate-50 dark:hover:bg-slate-800/40';
}

// ============================================
// Framer Motion Variants
// ============================================

const rowVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: { duration: 0.2, delay: i * 0.03 },
  }),
  exit: { opacity: 0, x: 10, transition: { duration: 0.15 } },
};

// ============================================
// Skeleton
// ============================================

export function FreePlanAccountsTableSkeleton() {
  return (
    <Card className="border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900 shadow-sm">
      <CardHeader className="pb-3 px-6 pt-5">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <Skeleton className="h-8 w-8 rounded-lg" />
            <div className="space-y-1">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-3 w-52" />
            </div>
          </div>
          <Skeleton className="h-9 w-56" />
        </div>
      </CardHeader>
      <CardContent className="px-0 pb-0">
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="flex items-center gap-4 px-6 py-3.5">
              <div className="flex-1 min-w-0 space-y-1">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="h-3 w-40 hidden md:block" />
              <Skeleton className="h-5 w-12 rounded-full" />
              <Skeleton className="h-3 w-20 hidden sm:block" />
              <Skeleton className="h-4 w-6" />
              <Skeleton className="h-5 w-14 rounded-full" />
              <Skeleton className="h-6 w-6 rounded-md" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================
// Component
// ============================================

export function FreePlanAccountsTable({
  accounts,
  isLoading,
  error,
  onViewAccount,
  onRetry,
}: FreePlanAccountsTableProps) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter(
      (a) =>
        a.tenantName.toLowerCase().includes(q) ||
        a.tenantSlug.toLowerCase().includes(q) ||
        (a.contactEmail?.toLowerCase().includes(q) ?? false)
    );
  }, [accounts, search]);

  // Sort by longest time on free plan first
  const sorted = useMemo(
    () => [...filtered].sort((a, b) => b.daysOnFreePlan - a.daysOnFreePlan),
    [filtered]
  );

  return (
    <Card className="border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
      {/* Header */}
      <CardHeader className="pb-3 px-6 pt-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <div className="h-8 w-8 rounded-lg bg-amber-50 dark:bg-amber-900/25 flex items-center justify-center flex-shrink-0">
              <Users className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                  Free Plan Accounts
                </h3>
                {!isLoading && !error && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                    {accounts.length}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Time-on-free tracking · sorted by longest tenure
              </p>
            </div>
          </div>

          {/* Search */}
          <div className="relative flex-shrink-0 sm:w-56">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              placeholder="Search accounts…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-8 text-xs bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
            />
          </div>
        </div>
      </CardHeader>

      <CardContent className="px-0 pb-0">
        {/* Error Banner */}
        {error && !isLoading && (
          <div className="mx-6 mb-3 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 dark:border-red-800/50 dark:bg-red-950/20">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-red-900 dark:text-red-200">
                Failed to load free plan accounts
              </p>
              <p className="mt-0.5 text-xs text-red-700/80 dark:text-red-400/80">
                {error.message}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 h-7 text-xs border-red-200 text-red-700 hover:bg-red-100 dark:border-red-700 dark:text-red-400"
              onClick={onRetry}
            >
              <RefreshCw className="mr-1 h-3 w-3" />
              Retry
            </Button>
          </div>
        )}

        {/* Table header */}
        {!isLoading && !error && accounts.length > 0 && (
          <div className="flex items-center gap-4 px-6 py-2 bg-slate-50 dark:bg-slate-800/50 border-y border-slate-100 dark:border-slate-800">
            <div className="flex-1 min-w-0">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                Account
              </span>
            </div>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 hidden md:block w-44">
              Contact
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 w-20 text-center">
              Days Free
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 hidden sm:block w-24">
              Joined
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 w-10 text-center">
              Users
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 w-16 text-center">
              Status
            </span>
            <span className="w-6" />
          </div>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="flex items-center gap-4 px-6 py-3.5">
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-4 w-36" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <Skeleton className="h-3 w-40 hidden md:block" />
                <Skeleton className="h-5 w-12 rounded-full" />
                <Skeleton className="h-3 w-20 hidden sm:block" />
                <Skeleton className="h-4 w-6" />
                <Skeleton className="h-5 w-14 rounded-full" />
                <Skeleton className="h-5 w-5 rounded" />
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && accounts.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="h-14 w-14 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center mb-3">
              <UserCheck className="h-7 w-7 text-emerald-500 dark:text-emerald-400" />
            </div>
            <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-1">
              All accounts are on paid plans
            </h4>
            <p className="text-xs text-slate-400 max-w-xs">
              No active accounts are currently on the free plan.
            </p>
          </div>
        )}

        {/* No search results */}
        {!isLoading && !error && accounts.length > 0 && sorted.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Search className="h-8 w-8 text-slate-300 dark:text-slate-600 mb-2" />
            <p className="text-sm text-slate-500 dark:text-slate-400">
              No accounts match <span className="font-medium">"{search}"</span>
            </p>
            <button
              onClick={() => setSearch('')}
              className="mt-1.5 text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              Clear search
            </button>
          </div>
        )}

        {/* Rows */}
        {!isLoading && !error && sorted.length > 0 && (
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            <AnimatePresence mode="popLayout">
              {sorted.map((account, i) => (
                <motion.div
                  key={account.tenantId}
                  custom={i}
                  variants={rowVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  onClick={() => onViewAccount(account.tenantId)}
                  className={cn(
                    'flex items-center gap-4 px-6 py-3.5 cursor-pointer transition-colors duration-100 group',
                    getRowUrgencyClass(account.daysOnFreePlan)
                  )}
                >
                  {/* Account name + slug */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                      {account.tenantName}
                    </p>
                    <p className="text-xs text-slate-400 dark:text-slate-500 truncate font-mono">
                      {account.tenantSlug}
                    </p>
                    {/* Non-current billing status shown inline on mobile */}
                    {account.billingStatus !== 'current' && (
                      <p className="text-[10px] text-orange-600 dark:text-orange-400 mt-0.5 sm:hidden">
                        Billing: {account.billingStatus}
                      </p>
                    )}
                  </div>

                  {/* Contact email */}
                  <div className="hidden md:block w-44 flex-shrink-0">
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                      {account.contactEmail ?? '—'}
                    </p>
                    {account.billingStatus !== 'current' && (
                      <p className="text-[10px] text-orange-500 dark:text-orange-400 mt-0.5">
                        {account.billingStatus}
                      </p>
                    )}
                  </div>

                  {/* Days on free */}
                  <div className="flex-shrink-0 w-20 flex justify-center">
                    <DaysOnFreeBadge days={account.daysOnFreePlan} />
                  </div>

                  {/* Joined date */}
                  <div className="hidden sm:block flex-shrink-0 w-24">
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {formatDate(account.joinedAt)}
                    </p>
                  </div>

                  {/* User count */}
                  <div className="flex-shrink-0 w-10 text-center">
                    <p className="text-xs font-medium tabular-nums text-slate-700 dark:text-slate-300">
                      {numberFormatter.format(account.userCount)}
                    </p>
                  </div>

                  {/* Status */}
                  <div className="flex-shrink-0 w-16 flex justify-center">
                    <StatusPill status={account.status} />
                  </div>

                  {/* Arrow */}
                  <ChevronRight className="h-4 w-4 text-slate-300 dark:text-slate-600 group-hover:text-indigo-500 dark:group-hover:text-indigo-400 group-hover:translate-x-0.5 transition-all duration-150 flex-shrink-0" />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Footer count */}
        {!isLoading && !error && sorted.length > 0 && (
          <div className="px-6 py-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
            <p className="text-xs text-slate-400">
              {search
                ? `${sorted.length} of ${accounts.length} accounts`
                : `${accounts.length} free plan account${accounts.length !== 1 ? 's' : ''} total`}
            </p>
            <div className="flex items-center gap-3 text-xs text-slate-400">
              <span className="flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-slate-400" />
                &lt;30d normal
              </span>
              <span className="flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-amber-400" />
                30–89d watch
              </span>
              <span className="flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-orange-400" />
                90–179d alert
              </span>
              <span className="flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-red-400" />
                180d+ critical
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
